// src/components/user/MainsTestSeries/TestsBySection.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { FileText, FileSearch, ArrowLeft } from "lucide-react";
import { toast } from "react-hot-toast";
import { API_BASE } from "../../../config"; // ✅ Import the API base URL

const user = JSON.parse(localStorage.getItem("user"));

const loadRazorpay = (src) =>
  new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const handleUpgradeClick = async () => {
  const res = await loadRazorpay(
    "https://checkout.razorpay.com/v1/checkout.js"
  );

  if (!res) {
    toast.error("Razorpay failed to load");
    return;
  }

  try {
    const orderRes = await axios.post(
      `${API_BASE}/api/payments/create-order`,
      {
        amount: 3999,
        type: "testsPremium",
      },
      {
        headers: { Authorization: `Bearer ${user.token}` },
      }
    );

    const { order } = orderRes.data;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: order.amount,
      currency: order.currency,
      name: "UPSC Journey",
      description: "Mains Test Series",
      order_id: order.id,

      handler: async function (response) {
        await axios.post(
          `${API_BASE}/api/payments/verify-payment`,
          {
            razorpay_order_id: order.id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            type: "testsPremium",
          },
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        );

        const updatedUser = { ...user, hasPaid: true };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        toast.success("Premium Activated!");
        window.location.reload();
      },
    };

    if (!window.Razorpay) {
  toast.error("Razorpay SDK not loaded");
  return;
}
    new window.Razorpay(options).open();
  } catch {
    toast.error("Payment failed");
  }
};

const TestsBySection = () => {
  const { section } = useParams();
  const [tests, setTests] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("user"))?.token;

  useEffect(() => {
    const fetchTests = async () => {
      try {
        const testsRes = await axios.get(
          `${API_BASE}/api/user/tests/mains/section/${section}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        console.log("✅ Section tests fetched:", testsRes.data);
        setTests(Array.isArray(testsRes.data) ? testsRes.data : []);

        // Try to fetch submissions, but don't fail if it errors
        try {
          const subsRes = await axios.get(
            `${API_BASE}/api/user/tests/mains/submissions`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const map = {};
          if (Array.isArray(subsRes.data)) {
            subsRes.data.forEach((s) => {
              if (s.testId && s.testId._id) {
                map[s.testId._id] = s;
              }
            });
          }
          setSubmissions(map);
          console.log("✅ Submissions loaded:", Object.keys(map).length);
        } catch (subErr) {
          console.warn(
            "⚠️ Submissions fetch failed, continuing without submission data:",
            subErr.message
          );
          setSubmissions({}); // Set empty submissions so tests still display
        }
      } catch (err) {
        console.error("❌ Error fetching tests:", err.message);
        toast.error("Failed to fetch tests");
      }
    };

    if (token) fetchTests();
  }, [section, token]);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Back Button */}
      <Button
        onClick={() => navigate("/user/tests/mains")}
        variant="outline"
        className="flex items-center gap-2"
      >
        <ArrowLeft size={16} />
        Back to Sections
      </Button>

      <h2 className="text-2xl font-bold text-center text-blue-800">
        📚 {section} – Available Tests
      </h2>

      {Array.isArray(tests) && tests.length > 0 ? (
  tests.map((test) => (
    <Card
      key={test._id}
      className={`relative border border-blue-200 transition-all duration-300
      ${test.hasAccess ? "hover:shadow-lg" : "opacity-80"}`}
    >
      {/* 🔒 PREMIUM OVERLAY */}
      {!test.hasAccess && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center text-white z-10 rounded-lg">
          <div className="text-2xl mb-2">🔒</div>
          <div className="font-semibold">Premium Test</div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleUpgradeClick();
            }}
            className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"
          >
            Upgrade Now
          </button>
        </div>
      )}

      <CardContent className="p-4 space-y-2">
        {/* Title + Badge */}
        <div className="flex justify-between items-center">
          <div className="text-blue-800 font-bold flex items-center gap-2 text-lg">
            <FileText size={18} />
            {test.title}
          </div>

          <span
            className={`text-xs px-2 py-1 rounded-full ${
              test.isFree
  ? "bg-green-100 text-green-700"
  : "bg-yellow-100 text-yellow-700"
            }`}
          >
            {test.isFree ? "Free" : "Premium"}
          </span>
        </div>

        {/* Meta Info */}
        <p className="text-sm text-gray-600">
          📅 {new Date(test.date).toLocaleDateString()}
        </p>

        {test.subject && (
          <p className="text-sm text-gray-600">📖 {test.subject}</p>
        )}

        {/* Button */}
        <Button
          className="mt-2"
          variant={submissions[test._id] ? "outline" : "default"}
          onClick={() => {
            if (!test.hasAccess) {
              toast.error("🔒 This is a premium test. Please upgrade.");
              return;
            }
            navigate(`/user/tests/mains/view/${test._id}`);
          }}
        >
          <FileSearch className="mr-2" size={16} />
          {submissions[test._id] ? "View Submission" : "Start Test"}
        </Button>
      </CardContent>
    </Card>
  ))
) : (
  <p className="text-gray-500 text-center italic mt-8">
    No tests found in this section.
  </p>
)}
    </div>
  );
};

export default TestsBySection;
